import { useAuth } from "@/_core/hooks/useAuth";
import SalviaLayout from "@/components/SalviaLayout";
import { Button } from "@/components/ui/button";
import { getModuleAccessByKey, type PortalShellModuleAccess, type PortalShellService } from "@/lib/portalShell";
import { CheckCircle2, FileText, Layers3, ShieldAlert, ShieldCheck, XCircle } from "lucide-react";
import { useLocation } from "wouter";

function StatusPill({ active, label }: { active: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
    >
      {active ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

export default function Contracts() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const platformNucleus = (user as any)?.platformNucleus;
  const services = (platformNucleus?.services || []) as PortalShellService[];
  const modules = (platformNucleus?.modules || []) as PortalShellModuleAccess[];
  const nr1Access = getModuleAccessByKey(modules, "nr1");

  return (
    <SalviaLayout title="Contratos">
      <div className="space-y-6">
        <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                <Layers3 className="h-3.5 w-3.5" />
                Administração da Plataforma
              </div>
              <div className="space-y-2">
                <h1 className="text-3xl font-semibold text-slate-900">Contratos, elegibilidade e leitura operacional</h1>
                <p className="max-w-3xl text-sm leading-6 text-slate-600">
                  Esta visão mostra a leitura contratual consolidada da plataforma, separando com clareza o que está
                  <strong> habilitado no canal</strong>, o que está <strong> efetivamente contratado</strong> para o
                  contexto atual e o que o usuário autenticado pode operar dentro do Portal PJC.
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setLocation("/modulos/nucleo")}>Voltar à Administração da Plataforma</Button>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-2">
          {services.map((service) => {
            const moduleAccess = modules.find((item) => item.moduleKey === service.moduleKey);
            const serviceLabel = service.serviceName || service.moduleKey.toUpperCase();

            return (
              <div key={service.moduleKey} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 space-y-4">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{serviceLabel}</h2>
                      <p className="mt-2 text-sm leading-6 text-slate-600">{service.contractLabel}</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <StatusPill active={!!service.channelHasService} label="Canal responsável habilitado" />
                      <StatusPill active={service.companyHasService} label="Contrato elegível" />
                      <StatusPill active={!!moduleAccess?.userHasVisibleAccess} label="Módulo visível" />
                      <StatusPill active={!!moduleAccess?.userHasOperationalAccess} label="Usuário pode operar" />
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-600">
                      <p>
                        <strong className="text-slate-900">Módulo derivado:</strong> {service.moduleKey.toUpperCase()}
                      </p>
                      <p className="mt-2">
                        <strong className="text-slate-900">Escopo estrutural:</strong> {moduleAccess?.scope || "não informado"}
                      </p>
                      <p className="mt-2">
                        <strong className="text-slate-900">Contratos de origem:</strong> {service.contractIds?.length ? service.contractIds.join(", ") : "nenhum contrato elegível"}
                      </p>
                      <p className="mt-2">
                        <strong className="text-slate-900">Canais responsáveis:</strong> {service.responsibleChannelIds?.length ? service.responsibleChannelIds.join(", ") : "nenhum canal responsável habilitado"}
                      </p>
                      <p className="mt-2">
                        <strong className="text-slate-900">Estado do módulo:</strong> {moduleAccess?.visibilityState === "operable"
                          ? "Visível e operável"
                          : moduleAccess?.visibilityState === "visible"
                            ? "Visível sem operação"
                            : "Não visível no contexto atual"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {services.length === 0 && (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-8 text-sm text-slate-600 lg:col-span-2">
              Não há serviços registrados no snapshot atual do portal para o contexto autenticado.
            </div>
          )}
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Regra aprovada</p>
            <h2 className="mt-2 text-xl font-semibold text-slate-900">A cadeia de decisão agora fica explícita</h2>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              O contrato passou a explicitar a cadeia decisória: empresa contratante, canal responsável, serviço ativo,
              visibilidade do módulo e, por fim, liberação operacional do usuário. Essa separação reduz ambiguidade e
              prepara a evolução futura de múltiplos módulos e perfis funcionais.
            </p>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-950 p-6 text-white shadow-sm">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-300">
              {nr1Access?.userHasOperationalAccess ? (
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
              ) : (
                <ShieldAlert className="h-4 w-4 text-amber-400" />
              )}
              Leitura operacional do NR1
            </div>
            <p className="mt-4 text-lg font-semibold">
              {nr1Access?.userHasOperationalAccess
                ? "O NR1 está visível e operável para este usuário no contexto atual."
                : nr1Access?.userHasVisibleAccess
                  ? "O NR1 está visível, mas ainda depende da camada operacional final para liberar ações críticas."
                  : "O NR1 ainda depende da combinação entre contrato válido, canal responsável e permissão operacional."}
            </p>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              No cenário aprovado do Portal PJC, esta tela mostra o estado contratual consolidado, enquanto a navegação
              operacional continua subordinada ao perfil autenticado.
            </p>
          </div>
        </section>
      </div>
    </SalviaLayout>
  );
}
